/*
 *   File : otp-input.js
 *   Author : https://evoqins.com
 *   Description : OTP input component
 *   Integrations : NA
 *   Version : 1.0.1
*/

//react
import { useEffect, forwardRef, memo, useState, useRef } from "react";

//styles
import styles from "../../Style/Components/otpInput.module.scss";
import { formatInputErrorMessage } from "../../Utils/format-input-error";

const OTPInput = forwardRef((props, ref) => {
  const {
    confirm_pin,
    focused_input,
    onBlur,
    onFocus,
    onChange,
    onKeyPress,
    label,
    labelStyle,
    require,
    id,
    spellCheck,
    autoFocus,
    tabIndex,
    value,
    error,
  } = props;

  const [pin, setPin] = useState([]);
  const otpInputRef = useRef(null);
  const input_width = useRef(0);

  // set width once
  useEffect(() => {
    if (otpInputRef.current) {
      input_width.current = otpInputRef.current.clientWidth;
    }
  }, []);

  // focus when needed
  useEffect(() => {
    if (ref && confirm_pin.length < 4) {
      ref.current.focus();
    }
  }, [ confirm_pin, ref]);

  // sync pin and blur when finished
  useEffect(() => {
    setPin(confirm_pin);
    // if (ref && confirm_pin.length === 5) {
    //   ref.current.blur();
    //   onBlur();
    // }
  }, [confirm_pin, ref, onBlur]);

  const _preventDefaults = (e) => {
    const key_code = e.keyCode || e.which;
    const is_ctrl_pressed = e.ctrlKey || e.metaKey;

    if (e.key === "Enter" && typeof onKeyPress === "function") {
      onKeyPress();
    }

    if (
      key_code === 9 || // Tab key
      (is_ctrl_pressed &&
        (key_code === 65 || // A
          key_code === 67 || // C
          key_code === 88 || // X
          key_code === 86)) // V
    ) {
      e.preventDefault();
    }

    if (
      (key_code >= 48 && key_code <= 57) || // 0-9
      (key_code >= 96 && key_code <= 105) || // Numpad 0-9
      key_code === 8 || // Backspace
      (key_code >= 37 && key_code <= 40) // Arrow keys
    ) {
      // Allow
    } else {
      e.preventDefault();
    }
  };

  return (
    <>
      {label && (
        <label
          className={`${
            labelStyle ? labelStyle : "fw-400 fs-12 lh-16"
          } text-eerie-black pb-2`}
        >
          {label}
          {require && <span className="text-cg-red">*</span>}
        </label>
      )}
      <div className={`${styles.e_otp_wrapper} h-48 user-select-none`}>
        <input
          style={{ width: input_width.current }}
          type="text"
          ref={ref}
          id={id}
          value={value}
          spellCheck={spellCheck}
          className="mb-0 e-caret-color-transparent"
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={_preventDefaults}
          autoComplete="off"
          autoFocus={autoFocus}
          tabIndex={tabIndex}
        />

        <label ref={otpInputRef} className="d-flex col-g-12">
          <div
            className={`justify-content-center border-gainsboro border-1  align-items-center w-48 h-48 fw-500 fs-16 e-inline-flex 
                  ${
                    focused_input === 2
                      ? "border-facebook-blue e-drop-shadow-black"
                      : "e-drop-shadow-black-22 border-gainsboro border-1 "
                  }`}
          >
            {pin.length !== 0 ? (
              <span className="pt-1 text-midnight-blue">*</span>
            ) : (
              <span className="pt-1 text-granite-gray opacity-50">*</span>
            )}
          </div>
          <div
            className={`justify-content-center border-1 align-items-center w-48 h-48 fw-500 fs-16 e-inline-flex
                  ${
                    focused_input === 2 && pin.length >= 2
                      ? "border-1 border-facebook-blue e-drop-shadow-black"
                      : "e-drop-shadow-black-22 border-gainsboro border-1  "
                  }`}
          >
            {pin.length >= 2 ? (
              <span className="pt-1 text-midnight-blue">*</span>
            ) : (
              <span className="pt-1 text-granite-gray opacity-50">*</span>
            )}
          </div>
          <div
            className={`justify-content-center border-1 align-items-center w-48 h-48 fw-500 fs-16 e-inline-flex
                 ${
                   focused_input === 2 && pin.length >= 3
                     ? "border-1 border-facebook-blue e-drop-shadow-black"
                     : "e-drop-shadow-black-22 border-gainsboro border-1 "
                 }`}
          >
            {pin.length >= 3 ? (
              <span className="pt-1 text-midnight-blue">*</span>
            ) : (
              <span className="pt-1 text-granite-gray opacity-50">*</span>
            )}
          </div>
          <div
            className={`justify-content-center border-1 align-items-center w-48 h-48 fw-500 fs-16 e-inline-flex 
                ${
                  focused_input === 2 && pin.length >= 4
                    ? "border-1 border-facebook-blue e-drop-shadow-black"
                    : "e-drop-shadow-black-22 border-gainsboro border-1 "
                }`}
          >
            {pin.length >= 4 ? (
              <span className="pt-1 text-midnight-blue">*</span>
            ) : (
              <span className="pt-1 text-granite-gray opacity-50">*</span>
            )}
          </div>
        </label>

        {error && (
          <p className="mb-0 text-cg-red fs-12 fw-400 ">
            {formatInputErrorMessage(error)}
          </p>
        )}
      </div>
    </>
  );
});
OTPInput.displayName = "OTPInput";

OTPInput.defaultProps = {
  onKeyPress: () => {},
  className: null,
  error: "",
};

export default memo(OTPInput);
